
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

type ChatRoom = Database['public']['Tables']['chat_rooms']['Row'];

export async function getChatRooms() {
  console.log("getChatRooms: Starting to fetch chat rooms");
  
  try {
    const result = await supabase
      .from("chat_rooms")
      .select("*")
      .order("created_at", { ascending: false });

    console.log("getChatRooms: Supabase query result:", result);
    return result;
  } catch (error) {
    console.error("getChatRooms: Unexpected error:", error);
    throw error;
  }
}

export function useChatRooms() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["chat-rooms"],
    queryFn: getChatRooms,
    retry: (failureCount, error) => {
      console.log("useChatRooms: Query failed, attempt", failureCount, "error:", error);
      return failureCount < 3;
    },
    retryDelay: 1000
  });

  const createRoomMutation = useMutation({
    mutationFn: async (roomData: { name: string; description?: string; is_private?: boolean }) => {
      if (!user) {
        throw new Error("Authentication required to create a room");
      }

      console.log("Creating chat room with data:", roomData);
      console.log("User creating room:", user.id);

      const { data, error } = await supabase
        .from("chat_rooms")
        .insert({
          ...roomData,
          created_by: user.id
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating chat room:", error);
        throw error;
      }

      console.log("Chat room created successfully:", data);
      
      // Verify that the trigger added the creator as a member for private rooms
      if (data.is_private) {
        console.log("Checking if creator was added as member for private room...");
        const { data: membershipData, error: membershipError } = await supabase
          .from("chat_room_members")
          .select("*")
          .eq("room_id", data.id)
          .eq("user_id", user.id);
          
        console.log("Membership check after room creation:", { membershipData, membershipError });
      }
      
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["chat-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["chat-room-members", data.id] });
      toast.success("Chat room created successfully");
    },
    onError: (error) => {
      console.error("Failed to create chat room:", error);
      toast.error("Failed to create chat room");
    }
  });

  return {
    ...query,
    createRoom: createRoomMutation.mutate,
    isCreatingRoom: createRoomMutation.isPending
  };
}
